class HeaderMenu {
  constructor() {
    this.state = {
      isMenuOpen: false,
      activeDesktopMenu: null,
      animationDuration: 300,
      lastScrollTop: 0,
      isHeaderVisible: true,
    };

    this.elements = {};
    this.timeouts = new Map();

    this.init();
  }

  init() {
    this.cacheElements();
    this.initMobileMenu();
    this.initDesktopMenus();
    this.initMobileSubmenus();
    this.createOverlay();
    this.bindGlobalEvents();
    this.initCartEvents();
  }

  cacheElements() {
    this.elements = {
      header: document.querySelector(".header"),
      menuButton: document.querySelector(".header-menu"),
      closeButton: document.querySelector(".menu-drawer__close-btn"),
      container: document.querySelector(".menu-drawer-container"),
      drawer: document.querySelector(".menu-drawer"),
      overlay: null,
      menuDrawerOverlay: document.querySelector(".header-menu-drawer-overlay"),
      megaMenus: document.querySelectorAll(".header__mega-menu-details"),
      regularMenus: document.querySelectorAll(
        ".header__inline-menu details:not(.header__mega-menu-details)"
      ),
      submenuToggles: document.querySelectorAll(".menu-drawer-title"),
      closeButtons: document.querySelectorAll(".menu-drawer__close-button"),
      cartCountBadge: document.querySelector(".cart-count-badge"),
    };
  }

  initMobileMenu() {
    const { menuButton, closeButton, menuDrawerOverlay } = this.elements;

    if (menuButton) {
      menuButton.addEventListener("click", this.handleMenuToggle.bind(this));
    }

    if (closeButton) {
      closeButton.addEventListener("click", this.handleMenuClose.bind(this));
    }

    if (menuDrawerOverlay) {
      menuDrawerOverlay.addEventListener(
        "click",
        this.handleMenuClose.bind(this)
      );
    }
  }

  handleMenuToggle(e) {
    e.preventDefault();
    e.stopPropagation();
    this.state.isMenuOpen ? this.closeMobileMenu() : this.openMobileMenu();
  }

  handleMenuClose(e) {
    e.preventDefault();
    e.stopPropagation();
    this.closeMobileMenu();
  }

  openMobileMenu() {
    const { container, drawer, menuDrawerOverlay } = this.elements;
    if (!container || !drawer || this.state.isMenuOpen) return;

    this.state.isMenuOpen = true;
    container.setAttribute("open", "");

    // Store current scroll position and prevent body scroll
    const currentScrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${currentScrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    requestAnimationFrame(() => {
      drawer.classList.add("menu-open");
      if (menuDrawerOverlay) {
        menuDrawerOverlay.classList.add("active");
      }
      if (this.elements.overlay) {
        this.elements.overlay.classList.add("active");
      }
    });
  }

  closeMobileMenu() {
    const { container, drawer, menuDrawerOverlay } = this.elements;
    if (!container || !drawer || !this.state.isMenuOpen) return;

    this.state.isMenuOpen = false;
    drawer.classList.remove("menu-open");
    if (menuDrawerOverlay) {
      menuDrawerOverlay.classList.remove("active");
    }
    if (this.elements.overlay) {
      this.elements.overlay.classList.remove("active");
    }

    // Restore scroll position and body scroll
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }

    this.clearTimeout("mobileMenuClose");
    this.setTimeout(
      "mobileMenuClose",
      () => {
        if (!this.state.isMenuOpen) {
          container.removeAttribute("open");
        }
      },
      this.state.animationDuration
    );
  }

  initDesktopMenus() {
    if (window.innerWidth < 768) return;
    this.initMegaMenus();
    this.initRegularMenus();
  }

  initMegaMenus() {
    this.elements.megaMenus.forEach((menu) => {
      const summary = menu.querySelector("summary");
      const dropdown = menu.querySelector(".header__mega-menu");

      if (!summary) return;

      const menuId = this.getMenuId(menu);
      let isHovered = false;

      const startOpening = () => {
        this.clearTimeout(menuId);
        this.closeOtherMegaMenus(menu);

        if (!menu.hasAttribute("open")) {
          menu.setAttribute("open", "");
          menu.classList.add("opening");

          const _ = dropdown.offsetHeight;

          const onOpenEnd = (ev) => {
            if (ev.target !== dropdown) return;
            dropdown.removeEventListener("transitionend", onOpenEnd);
            menu.classList.remove("opening");
          };
          dropdown.addEventListener("transitionend", onOpenEnd);
        }

        this.state.activeDesktopMenu = menu;
        isHovered = true;
      };

      const startClosing = () => {
        isHovered = false;
        this.setTimeout(
          menuId,
          () => {
            if (isHovered || !menu.hasAttribute("open")) return;

            menu.classList.add("closing");

            const onEnd = (ev) => {
              if (ev.target !== dropdown) return;
              dropdown.removeEventListener("transitionend", onEnd);
              menu.classList.remove("closing");
              if (menu.hasAttribute("open")) menu.removeAttribute("open");

              if (this.state.activeDesktopMenu === menu) {
                this.state.activeDesktopMenu = null;
              }

              this.clearTimeout(`${menuId}-force-close`);
            };

            dropdown.addEventListener("transitionend", onEnd);

            this.setTimeout(
              `${menuId}-force-close`,
              () => {
                dropdown.removeEventListener("transitionend", onEnd);
                menu.classList.remove("closing");
                if (menu.hasAttribute("open")) menu.removeAttribute("open");
                if (this.state.activeDesktopMenu === menu)
                  this.state.activeDesktopMenu = null;
              },
              this.state.animationDuration + 50
            );
          },
          120
        );
      };

      summary.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });

      summary.addEventListener("mouseenter", startOpening);
      summary.addEventListener("mouseleave", startClosing);

      if (dropdown) {
        dropdown.addEventListener("mouseenter", () => {
          isHovered = true;
          this.clearTimeout(menuId);
        });
        dropdown.addEventListener("mouseleave", startClosing);
      }

      summary.addEventListener("focus", startOpening);
      summary.addEventListener("blur", () => {
        this.setTimeout(
          `${menuId}-blur`,
          () => {
            if (!menu.contains(document.activeElement)) {
              startClosing();
            }
          },
          100
        );
      });
    });
  }

  initRegularMenus() {
    this.elements.regularMenus.forEach((menu) => {
      const summary = menu.querySelector("summary");
      const submenu = menu.querySelector(".header__submenu");

      if (!summary) return;

      const menuId = this.getMenuId(menu);

      const openMenu = () => {
        this.clearTimeout(menuId);
        if (!menu.hasAttribute("open")) {
          menu.setAttribute("open", "");
        }
      };

      const closeMenu = () => {
        this.setTimeout(
          menuId,
          () => {
            menu.removeAttribute("open");
          },
          150
        );
      };

      // Prevent default click behavior and handle hover
      summary.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });

      summary.addEventListener("mouseenter", openMenu);
      summary.addEventListener("mouseleave", closeMenu);

      if (submenu) {
        submenu.addEventListener("mouseenter", () => this.clearTimeout(menuId));
        submenu.addEventListener("mouseleave", closeMenu);
      }
    });
  }

  closeOtherMegaMenus(currentMenu) {
    this.elements.megaMenus.forEach((menu) => {
      if (menu !== currentMenu && menu.hasAttribute("open")) {
        menu.removeAttribute("open");
      }
    });
  }

  initMobileSubmenus() {
    this.elements.submenuToggles.forEach((toggle) => {
      toggle.addEventListener("click", this.handleSubmenuToggle.bind(this));
    });

    this.elements.closeButtons.forEach((button) => {
      button.addEventListener("click", this.handleSubmenuClose.bind(this));
    });

    // Initialize open submenus with proper height
    const openSubmenus = document.querySelectorAll(
      ".menu-drawer details[open]"
    );
    openSubmenus.forEach((details) => {
      const submenu = details.querySelector(".menu-drawer__submenu");
      const summary = details.querySelector("summary");

      if (submenu && summary) {
        // Drawer is hidden at load (scrollHeight is 0), so leave the height uncapped;
        // closeSubmenu pins the real height before collapsing
        submenu.style.maxHeight = "none";

        details.classList.add("is-expanded");
      }
    });
  }

  handleSubmenuToggle(e) {
    // Heading link inside the summary navigates instead of toggling
    if (e.target.closest(".menu-drawer-title__link")) return;

    e.preventDefault();
    e.stopPropagation();

    const details = e.target.closest("details");
    const submenu = details?.querySelector(".menu-drawer__submenu");

    if (!details || !submenu) return;

    const isOpen = details.classList.contains("is-expanded");

    if (isOpen) {
      this.closeSubmenu(details, submenu);
    } else {
      this.closeOtherSubmenus(details);
      this.openSubmenu(details, submenu);
    }
  }

  handleSubmenuClose(e) {
    e.preventDefault();
    e.stopPropagation();

    const details = e.target.closest("details");
    const submenu = details?.querySelector(".menu-drawer__submenu");

    if (details && submenu) {
      this.closeSubmenu(details, submenu);
    }
  }

  openSubmenu(details, submenu) {
    details.setAttribute("open", "");
    details.classList.add("is-expanded");

    requestAnimationFrame(() => {
      const scrollHeight = submenu.scrollHeight;
      submenu.style.maxHeight = scrollHeight + "px";
    });
  }

  closeSubmenu(details, submenu) {
    details.classList.remove("is-expanded");

    // Pin the current height first so the collapse animates from it
    submenu.style.maxHeight = submenu.scrollHeight + "px";
    submenu.offsetHeight;
    submenu.style.maxHeight = "0px";

    // Per-submenu timer; skipped if the submenu was reopened mid-animation
    setTimeout(() => {
      if (details.classList.contains("is-expanded")) return;
      details.removeAttribute("open");
      submenu.style.maxHeight = "";
    }, this.state.animationDuration);
  }

  closeOtherSubmenus(currentDetails) {
    const openSubmenus = document.querySelectorAll(
      ".menu-drawer details.is-expanded"
    );

    openSubmenus.forEach((details) => {
      if (details !== currentDetails) {
        const submenu = details.querySelector(".menu-drawer__submenu");
        if (submenu) {
          this.closeSubmenu(details, submenu);
        }
      }
    });
  }

  createOverlay() {
    let overlay = document.querySelector(".menu-overlay");

    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "menu-overlay";
      document.body.appendChild(overlay);
    }

    this.elements.overlay = overlay;
    overlay.addEventListener("click", () => {
      if (this.state.isMenuOpen) {
        this.closeMobileMenu();
      }
    });
  }

  bindGlobalEvents() {
    document.addEventListener("keydown", this.handleKeydown.bind(this));
    window.addEventListener("resize", this.handleResize.bind(this));
    document.addEventListener("click", this.handleOutsideClick.bind(this));
  }

  handleKeydown(e) {
    if (e.key !== "Escape") return;

    if (this.state.isMenuOpen) {
      this.closeMobileMenu();
    }
    this.closeAllDesktopMenus({ restoreFocus: true });
  }

  handleResize() {
    if (window.innerWidth >= 768 && this.state.isMenuOpen) {
      this.closeMobileMenu();
    }
  }

  handleOutsideClick(e) {
    if (!e.target.closest(".header")) {
      this.closeAllDesktopMenus();
    }
  }

  closeAllDesktopMenus({ restoreFocus = false } = {}) {
    const openMenus = document.querySelectorAll(
      ".header__mega-menu-details[open], .header__inline-menu details[open]"
    );

    openMenus.forEach((menu) => {
      menu.removeAttribute("open");
      if (restoreFocus) menu.querySelector("summary")?.focus();
    });
  }

  getMenuId(element) {
    return (
      element.dataset.menuId ||
      `menu-${Math.random().toString(36).substr(2, 9)}`
    );
  }

  setTimeout(key, callback, delay) {
    this.clearTimeout(key);
    this.timeouts.set(key, setTimeout(callback, delay));
  }

  clearTimeout(key) {
    const timeout = this.timeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(key);
    }
  }

  initCartEvents() {
    window.addEventListener("cart:updated", this.handleCartUpdate.bind(this));

    document.addEventListener(
      "cart:item-added",
      this.handleCartUpdate.bind(this)
    );
  }

  toggleSearch() {
    // Try multiple possible selectors for search modal
    const searchModal = document.querySelector(
      ".search-modal, .header-search, .predictive-search-modal"
    );
    const searchOverlay = document.querySelector(
      ".search-overlay, .header-search-overlay, .search-modal-overlay"
    );
    const searchInput = document.querySelector(
      '.search-input, .search-modal input[type="search"], input[name="q"]'
    );

    if (!searchModal) {
      console.warn(
        "No search modal found. Available selectors checked: .search-modal, .header-search, .predictive-search-modal"
      );
      return;
    }

    const isOpen =
      searchModal.classList.contains("active") ||
      searchModal.classList.contains("open");

    if (isOpen) {
      // Close search
      searchModal.classList.remove("active", "open");
      if (searchOverlay) {
        searchOverlay.classList.remove("active", "open");
      }
      document.body.style.overflow = "";
      document.body.classList.remove("search-open");
    } else {
      // Open search
      searchModal.classList.add("active");
      if (searchOverlay) {
        searchOverlay.classList.add("active");
      }
      document.body.style.overflow = "hidden";
      document.body.classList.add("search-open");

      // Focus search input
      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 100);
      } else {
        console.warn("Search input not found for focus");
      }
    }
  }

  async handleCartUpdate(event) {
    try {
      let cart;

      if (event && event.detail && event.detail.cart) {
        cart = event.detail.cart;
      } else {
        const response = await fetch("/cart.js");
        cart = await response.json();

        window.dispatchEvent(
          new CustomEvent("cart:count-update", {
            detail: {
              count: cart.item_count, // The number of cart items
            },
          })
        );
      }

      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error("Failed to update cart count:", error);
    }
  }

  updateCartCount(count) {
    const { cartCountBadge } = this.elements;

    if (!cartCountBadge) return;

    const currentCount = parseInt(cartCountBadge.textContent) || 0;
    const newCount = parseInt(count) || 0;

    cartCountBadge.textContent = newCount;

    if (newCount > 0) {
      cartCountBadge.style.display = "";

      if (newCount > currentCount) {
        cartCountBadge.classList.add("updating");

        setTimeout(() => {
          cartCountBadge.classList.remove("updating");
        }, 600);
      }
    } else {
      cartCountBadge.style.display = "none";
    }
  }

  destroy() {
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts.clear();

    if (this.elements.overlay) {
      this.elements.overlay.remove();
    }

    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
  }
}

let headerMenuInstance;

function initHeaderMenu() {
  try {
    headerMenuInstance = new HeaderMenu();
  } catch (error) {
    console.error("Failed to initialize header menu:", error);
  }
}

// Global function for search toggle (called from HTML onclick)
window.toggleSearch = function () {
  if (headerMenuInstance && headerMenuInstance.toggleSearch) {
    headerMenuInstance.toggleSearch();
  } else {
    // Fallback if header menu not ready yet
    console.warn("Header menu not ready, using fallback toggleSearch");
    toggleSearchFallback();
  }
};

// Fallback search toggle function
function toggleSearchFallback() {
  const searchModal = document.querySelector(
    ".search-modal, .header-search, .predictive-search-modal"
  );
  const searchOverlay = document.querySelector(
    ".search-overlay, .header-search-overlay, .search-modal-overlay"
  );
  const searchInput = document.querySelector(
    '.search-input, .search-modal input[type="search"], input[name="q"]'
  );

  if (!searchModal) {
    console.warn("Fallback: No search modal found");
    return;
  }

  const isOpen =
    searchModal.classList.contains("active") ||
    searchModal.classList.contains("open");

  if (isOpen) {
    // Close search
    searchModal.classList.remove("active", "open");
    if (searchOverlay) {
      searchOverlay.classList.remove("active", "open");
    }
    document.body.style.overflow = "";
    document.body.classList.remove("search-open");
  } else {
    // Open search
    searchModal.classList.add("active");
    if (searchOverlay) {
      searchOverlay.classList.add("active");
    }
    document.body.style.overflow = "hidden";
    document.body.classList.add("search-open");

    // Focus search input
    if (searchInput) {
      setTimeout(() => searchInput.focus(), 100);
    }
  }
}

// Also make it available without window prefix for backward compatibility
function toggleSearch() {
  window.toggleSearch();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderMenu);
} else {
  initHeaderMenu();
}
